import {
  CONTROL_ROW_HEIGHT_CLASS,
  TOOLBAR_BUTTON_SIZE_CLASS,
  VOLUME_DIVIDER_HEIGHT_CLASS,
  type PlayerControlSize,
} from "app/player/controlSizes"
import { cva } from "class-variance-authority"

export type VolumeControlSize = PlayerControlSize

const VOLUME_BUTTON_SIZE_CLASS: Record<PlayerControlSize, string> = {
  sm: TOOLBAR_BUTTON_SIZE_CLASS.sm,
  md: TOOLBAR_BUTTON_SIZE_CLASS.md,
}

/** Width-only — height comes from the bordered volume container via h-full. */
const VOLUME_ICON_SECTION_WIDTH_CLASS: Record<PlayerControlSize, string> = {
  sm: "w-10",
  md: "w-12",
}

export const playerIconButtonInnerClassName =
  "flex flex-1 items-center justify-center rounded-[12px] border border-solid border-gray-75 bg-white transition-colors group-hover:border-gray-200 group-hover:bg-gray-75 group-active:border-gray-200 group-active:bg-gray-75"

export const blueVolumeIconButtonBaseClassName =
  "inline-flex shrink-0 items-center justify-center rounded-[12px] border-[1px] border-black text-white shadow-[0_0_4px_rgba(0,0,0,0.25)] transition-colors"

export const blueVolumeIconButtonSizeClassName = cva("", {
  variants: {
    size: VOLUME_BUTTON_SIZE_CLASS,
  },
  defaultVariants: {
    size: "md",
  },
})

export const blueVolumeIconButtonActiveClassName =
  "bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(#03004e,#03004e)] hover:bg-[linear-gradient(rgba(255,65,0,0.3),rgba(255,65,0,0.3)),linear-gradient(#03004e,#03004e)] active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(#03004e,#03004e)]"

export const blueVolumeIconButtonDefaultClassName =
  "bg-neutraal-900 hover:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(#03004e,#03004e)] active:bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(#03004e,#03004e)]"

export const whiteVolumeIconButtonActiveClassName =
  "bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(white,white)] hover:bg-[linear-gradient(rgba(255,65,0,0.3),rgba(255,65,0,0.3)),linear-gradient(white,white)] active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(white,white)]"

export const whiteVolumeIconButtonDefaultClassName =
  "bg-white hover:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(white,white)] active:bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(white,white)]"

export const iconSectionVariants = cva(
  "group flex h-full shrink-0 items-center justify-center rounded-bl-[12px] rounded-tl-[12px] transition-colors",
  {
    variants: {
      color: {
        blue: "",
        gray: "",
        white: "",
      },
      muted: {
        true: "",
        false: "",
      },
      size: VOLUME_ICON_SECTION_WIDTH_CLASS,
    },
    compoundVariants: [
      {
        color: "blue",
        muted: false,
        class:
          "hover:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(#03004e,#03004e)] active:bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(#03004e,#03004e)]",
      },
      {
        color: "blue",
        muted: true,
        class: blueVolumeIconButtonActiveClassName,
      },
      {
        color: "white",
        muted: false,
        class:
          "hover:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(white,white)] active:bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(white,white)]",
      },
      {
        color: "white",
        muted: true,
        class: whiteVolumeIconButtonActiveClassName,
      },
    ],
    defaultVariants: {
      color: "blue",
      muted: false,
      size: "md",
    },
  }
)

export const containerVariants = cva(
  "flex items-center overflow-hidden drop-shadow-[0_0_4px_rgba(0,0,0,0.25)]",
  {
    variants: {
      color: {
        blue: "border-[1px] border-black bg-neutraal-900",
        gray: "border border-gray-400 bg-white",
        white: "border-[1px] border-black bg-white",
      },
      orientation: {
        horizontal: "rounded-[12px]",
        vertical: "flex-col rounded-[12px]",
      },
      size: {
        sm: "",
        md: "",
      },
    },
    compoundVariants: [
      {
        orientation: "horizontal",
        size: "sm",
        class: CONTROL_ROW_HEIGHT_CLASS.sm,
      },
      {
        orientation: "horizontal",
        size: "md",
        class: CONTROL_ROW_HEIGHT_CLASS.md,
      },
    ],
    defaultVariants: {
      color: "blue",
      orientation: "horizontal",
      size: "md",
    },
  }
)

export const dividerVariants = cva("w-px shrink-0", {
  variants: {
    color: {
      blue: "bg-white/20",
      gray: "bg-black/20",
      white: "bg-black/20",
    },
    orientation: {
      horizontal: "",
      vertical: "hidden",
    },
    size: VOLUME_DIVIDER_HEIGHT_CLASS,
  },
  defaultVariants: {
    color: "blue",
    orientation: "horizontal",
    size: "md",
  },
})

export const labelVariants = cva(
  "w-[34px] shrink-0 text-center text-[15px] leading-normal font-semibold",
  {
    variants: {
      color: {
        blue: "text-white",
        gray: "text-black",
        white: "text-black",
      },
    },
    defaultVariants: {
      color: "blue",
    },
  }
)

export const grayVolumeTriggerClassName =
  "group flex size-12 w-full shrink-0 flex-col items-stretch rounded-[12px] border-[1px] border-gray-400 bg-gray-75 p-0 pb-1 text-neutraal-900 shadow-[0_2px_0_rgba(0,0,0,0.1)] transition-colors hover:bg-gray-200 active:bg-gray-75"

export const grayVolumeMutedActiveClassName =
  "bg-[linear-gradient(rgba(255,65,0,0.15),rgba(255,65,0,0.15)),linear-gradient(#ebebeb,#ebebeb)] hover:bg-[linear-gradient(rgba(255,65,0,0.25),rgba(255,65,0,0.25)),linear-gradient(#ebebeb,#ebebeb)] active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(#ebebeb,#ebebeb)]"

export const grayVolumeInnerMutedActiveClassName =
  "border-orange-200 bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(white,white)]"

export const VERTICAL_COLLAPSED_HEIGHT = 48
export const VERTICAL_EXPANDED_HEIGHT = 242
export const VOLUME_COLLAPSE_DURATION = 0.1
